import { useState, useRef, useEffect } from 'react'
import { Check, FolderPlus } from 'lucide-react'
import { addFolderListItem } from '../../services/api'
import type { Project } from '../../types'

interface AttachToProjectMenuProps {
  projects?: Project[]
  journalId: string
  journalTitle: string
}

/**
 * "Attach to project" for a journal — reuses the real folder-lists API
 * (resource_type='journal' is one of the three types folder_lists/handler.py
 * actually accepts) with the project's root folder, using the SAME
 * folder_id === project_id convention ProjectView.tsx already relies on for
 * its own saved contacts/organisations lists (see
 * getFolderListItems(projectId, projectId, 'contact')). No new backend
 * capability invented — just the existing mechanism, applied here.
 */
export default function AttachToProjectMenu({ projects, journalId, journalTitle }: AttachToProjectMenuProps) {
  const [open, setOpen] = useState(false)
  const [added, setAdded] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleAttach = async (project: Project) => {
    setError(null)
    setSaving(project.project_id)
    try {
      await addFolderListItem(project.project_id, project.project_id, {
        resource_type: 'journal',
        resource_id: journalId,
        title: journalTitle,
      })
      setAdded(project.project_id)
      setTimeout(() => {
        setAdded(null)
        setOpen(false)
      }, 1200)
    } catch (err) {
      // Mirrors the CardAddToList 409-is-benign pattern: attaching a journal
      // already attached to this project is not a real failure.
      const e = err as { response?: { status?: number; data?: { error?: string } }; message?: string }
      if (e?.response?.status === 409) {
        setAdded(project.project_id)
        setTimeout(() => {
          setAdded(null)
          setOpen(false)
        }, 1200)
      } else {
        setError(e?.response?.data?.error || e?.message || 'Failed to attach to project')
      }
    } finally {
      setSaving(null)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation()
          setOpen(!open)
        }}
        className="p-1.5 text-gray-500 hover:text-iflow-600 flex-shrink-0"
        title="Attach to project"
      >
        <FolderPlus size={14} />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-52 light-card shadow-lg z-20 py-1"
          onClick={(e) => e.stopPropagation()}
        >
          {!projects || projects.length === 0 ? (
            <p className="px-3 py-2 text-xs text-gray-500">No projects yet.</p>
          ) : (
            projects.map((p) => (
              <button
                key={p.project_id}
                onClick={() => handleAttach(p)}
                disabled={saving === p.project_id}
                className="w-full flex items-center justify-between px-3 py-1.5 text-xs text-gray-700 hover:bg-white/50 disabled:opacity-50"
              >
                <span className="truncate">{p.name}</span>
                {added === p.project_id && <Check size={12} className="text-green-500 flex-shrink-0" />}
              </button>
            ))
          )}
          {error && (
            <p data-testid="attach-project-error" className="px-3 py-2 text-xs text-red-600 border-t border-gray-100">{error}</p>
          )}
        </div>
      )}
    </div>
  )
}
